import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth, formatErr } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, Truck, Trash2, Users, Key, Copy, RefreshCw, Save } from "lucide-react";

const CATS = ["elettricista", "idraulico", "frigorista"];

export default function AdminFurgoni() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [furgoni, setFurgoni] = useState([]);
  const [operai, setOperai] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [pairInfo, setPairInfo] = useState(null); // { furgone, code, expires_at }
  const isStaff = user?.role === "personale" || user?.role === "admin";
  const isAdmin = user?.role === "admin";

  useEffect(() => { if (!loading && !isStaff) navigate("/login"); }, [user, loading, navigate, isStaff]);

  const load = async () => {
    setBusy(true);
    try {
      const [f, o] = await Promise.all([
        api.get("/admin/furgoni"),
        api.get("/furgone-members"),
      ]);
      setFurgoni(f.data);
      setOperai(o.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [user]);

  const savePatch = async (f, patch) => {
    try {
      await api.patch(`/admin/furgoni/${f.furgone_id}`, patch);
      toast.success("Salvato");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (f) => {
    if (!window.confirm(`Eliminare il furgone "${f.name}"? I tablet associati dovranno essere ri-associati.`)) return;
    try { await api.delete(`/admin/furgoni/${f.furgone_id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const generatePair = async (f) => {
    try {
      const r = await api.post(`/admin/furgoni/${f.furgone_id}/pair-code`);
      setPairInfo({ furgone: f, code: r.data.code, expires_at: r.data.expires_at });
    } catch (e) { toast.error(formatErr(e)); }
  };

  const resetPin = async (op) => {
    if (!window.confirm(`Cancellare il PIN di ${op.name}? Al prossimo accesso dovrà rifare il primo login con email e password.`)) return;
    try { await api.post(`/admin/users/${op.user_id}/reset-pin`); toast.success("PIN cancellato"); }
    catch (e) { toast.error(formatErr(e)); }
  };

  if (!isStaff) return <div className="min-h-screen"><Navbar/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mt-6 mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold">Tablet PWA</div>
            <h1 className="heading text-3xl md:text-4xl font-extrabold text-slate-900 flex items-center gap-2">
              <Truck className="w-7 h-7"/> Furgoni
            </h1>
            <p className="text-sm text-slate-600 mt-1">Crea i furgoni, assegna operai (per il tablet condiviso) o personale/admin (per il PWA /mobile sul cellulare), e genera i codici di associazione.</p>
          </div>
          <Button onClick={() => setNewOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="furgone-new-btn">
            <Plus className="w-4 h-4 mr-2"/> Nuovo furgone
          </Button>
        </div>

        {busy && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-800 mx-auto"/></div>}
        {!busy && furgoni.length === 0 && (
          <Card className="p-12 text-center border-dashed">
            <Truck className="w-10 h-10 text-slate-300 mx-auto mb-2"/>
            <p className="text-slate-600">Nessun furgone. Creane uno per iniziare a usare i tablet in cantiere.</p>
            <Button onClick={() => setNewOpen(true)} className="mt-4 bg-blue-800">Crea il primo</Button>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {furgoni.map(f => (
            <FurgoneCard key={f.furgone_id} f={f} operai={operai} isAdmin={isAdmin}
                         onSave={(patch) => savePatch(f, patch)}
                         onDelete={() => del(f)}
                         onPair={() => generatePair(f)}
                         onResetPin={resetPin}/>
          ))}
        </div>
      </div>

      <NewFurgoneDialog open={newOpen} onOpenChange={setNewOpen} operai={operai} onCreated={load}/>
      <PairCodeDialog info={pairInfo} onClose={() => setPairInfo(null)}/>
    </div>
  );
}

function FurgoneCard({ f, operai, onSave, onDelete, onPair, onResetPin, isAdmin }) {
  const [name, setName] = useState(f.name);
  const [category, setCategory] = useState(f.category || "");
  const [opIds, setOpIds] = useState(f.operaio_ids || []);
  const dirty = name !== f.name || category !== (f.category || "") || JSON.stringify(opIds) !== JSON.stringify(f.operaio_ids || []);
  const toggleOp = (uid) => setOpIds(o => o.includes(uid) ? o.filter(x => x !== uid) : [...o, uid]);

  return (
    <Card className="p-5 space-y-3" data-testid={`furgone-${f.furgone_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Nome furgone</label>
          <Input value={name} onChange={e=>setName(e.target.value)} data-testid={`f-name-${f.furgone_id}`}/>
        </div>
        <div className="w-40">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">Squadra</label>
          <Select value={category || "none"} onValueChange={v => setCategory(v === "none" ? "" : v)}>
            <SelectTrigger data-testid={`f-cat-${f.furgone_id}`}><SelectValue placeholder="—"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            <Users className="w-3 h-3 inline mr-1"/> Membri del furgone ({opIds.length})
          </label>
        </div>
        <p className="text-[11px] text-slate-500 mb-1.5">Operai per il tablet condiviso; personale e admin per il PWA /mobile sul proprio cellulare.</p>
        <div className="flex flex-wrap gap-1.5">
          {operai.length === 0 && <span className="text-xs text-slate-400">Nessun utente disponibile. Creane uno da /admin/utenti</span>}
          {operai.map(op => {
            const active = opIds.includes(op.user_id);
            const rb = op.role === "admin" ? "bg-amber-500" : op.role === "personale" ? "bg-blue-500" : "bg-slate-400";
            return (
              <button key={op.user_id} onClick={() => toggleOp(op.user_id)}
                      className={`text-xs px-2 py-1 rounded-full border inline-flex items-center gap-1 ${active ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}
                      data-testid={`f-op-${f.furgone_id}-${op.user_id}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${rb}`}/>
                {op.name}
              </button>
            );
          })}
        </div>
      </div>

      {f.operai && f.operai.length > 0 && isAdmin && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-800">Gestione PIN operai</summary>
          <div className="mt-2 space-y-1">
            {f.operai.map(op => (
              <div key={op.user_id} className="flex items-center justify-between text-slate-700 border-b border-slate-100 py-1">
                <span>{op.name}</span>
                <button onClick={() => onResetPin(op)} className="text-red-600 hover:underline text-[11px]" data-testid={`f-pin-reset-${op.user_id}`}>
                  Cancella PIN
                </button>
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="flex flex-wrap gap-2 pt-1 border-t">
        <Button size="sm" onClick={() => onSave({ name, category: category || null, operaio_ids: opIds })} disabled={!dirty} className="bg-blue-800 disabled:opacity-40" data-testid={`f-save-${f.furgone_id}`}>
          <Save className="w-3.5 h-3.5 mr-1"/> Salva modifiche
        </Button>
        <Button size="sm" onClick={onPair} variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100" data-testid={`f-pair-${f.furgone_id}`}>
          <Key className="w-3.5 h-3.5 mr-1"/> Genera codice tablet
        </Button>
        <div className="text-xs text-slate-500 flex items-center gap-2 ml-auto">
          <span>{f.device_count || 0} tablet associati</span>
          {isAdmin && (
            <button onClick={onDelete} className="p-1 text-slate-400 hover:text-red-600" data-testid={`f-del-${f.furgone_id}`}>
              <Trash2 className="w-4 h-4"/>
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

function NewFurgoneDialog({ open, onOpenChange, operai, onCreated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [opIds, setOpIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setName(""); setCategory(""); setOpIds([]); } }, [open]);

  const create = async () => {
    if (!name.trim()) { toast.error("Inserisci il nome"); return; }
    setBusy(true);
    try {
      await api.post("/admin/furgoni", { name, category: category || null, operaio_ids: opIds });
      toast.success("Furgone creato");
      onOpenChange(false); onCreated();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="heading">Nuovo furgone</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Nome</label>
            <Input value={name} onChange={e=>setName(e.target.value)} placeholder="Es: Furgone 1 - Elettrico" data-testid="new-furgone-name"/>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Squadra (opzionale)</label>
            <Select value={category || "none"} onValueChange={v => setCategory(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="new-furgone-cat"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1 block">Membri del furgone</label>
            <p className="text-[11px] text-slate-500 mb-1.5">Operai (tablet condiviso) + personale/admin (PWA mobile personale).</p>
            <div className="flex flex-wrap gap-1.5" data-testid="new-furgone-operai">
              {operai.map(op => {
                const active = opIds.includes(op.user_id);
                const rb = op.role === "admin" ? "bg-amber-500" : op.role === "personale" ? "bg-blue-500" : "bg-slate-400";
                return (
                  <button key={op.user_id} onClick={() => setOpIds(o => active ? o.filter(x => x !== op.user_id) : [...o, op.user_id])}
                          className={`text-xs px-2 py-1 rounded-full border inline-flex items-center gap-1 ${active ? "bg-blue-800 text-white border-blue-800" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${rb}`}/>
                    {op.name}
                  </button>
                );
              })}
            </div>
          </div>
          <Button onClick={create} disabled={busy || !name.trim()} className="w-full bg-blue-800 hover:bg-blue-900" data-testid="new-furgone-create">
            {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Crea furgone"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PairCodeDialog({ info, onClose }) {
  const copy = () => {
    navigator.clipboard.writeText(info.code);
    toast.success("Codice copiato");
  };
  const url = typeof window !== "undefined" ? `${window.location.origin}/tablet` : "";
  return (
    <Dialog open={!!info} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><Key className="w-5 h-5 text-emerald-600"/>Codice di associazione tablet</DialogTitle>
        </DialogHeader>
        {info && (
          <div className="space-y-3 mt-2">
            <p className="text-sm text-slate-600">
              Sul tablet apri <span className="font-mono bg-slate-100 px-1 rounded">{url}</span> (o sul cellulare personale <span className="font-mono bg-slate-100 px-1 rounded">{typeof window !== "undefined" ? `${window.location.origin}/mobile` : ""}</span>) e inserisci questo codice.
              Valido 15 minuti, uso singolo.
            </p>
            <div className="text-center bg-gradient-to-br from-blue-800 to-blue-600 text-white rounded-xl p-6" data-testid="pair-code-display">
              <div className="text-[10px] uppercase tracking-widest text-blue-200 mb-1">{info.furgone.name}</div>
              <div className="text-5xl font-bold tracking-[0.3em] font-mono">{info.code}</div>
            </div>
            <div className="flex gap-2">
              <Button onClick={copy} variant="outline" className="flex-1" data-testid="pair-code-copy">
                <Copy className="w-4 h-4 mr-2"/> Copia
              </Button>
              <Button onClick={onClose} className="flex-1 bg-blue-800">Ho fatto</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
